import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageShell } from "../components/Layout";
import SEO from "../components/SEO";
import { useAuth } from "../context/AuthContext";
import { startGoogleSignIn } from "../lib/capacitorAuth";
import api, { resolveAssetUrl } from "../lib/api";
import PortalAuthModal from "../components/PortalAuthModal";
import {
  Sparkle, ShieldCheck, MoonStars, ChatCircleDots, Lightning, Star,
  CaretDown, Robot, Envelope,
} from "@phosphor-icons/react";

// Public /login page. Doubles as a high-intent SEO landing for the
// keyword "free astrology chat AI" — hence the hero, astrologer grid
// and FAQ section all speak to that phrase.
export default function LoginPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  // If already signed in, don't sit on /login.
  useEffect(() => {
    if (!loading && user) navigate("/astrologers", { replace: true });
  }, [user, loading, navigate]);

  const firstChatFree = !user?.free_trial_used;

  const handleSignIn = () => {
    const redirectUrl = window.location.origin + "/astrologers";
    startGoogleSignIn(redirectUrl);
  };

  // ─── Portal (email + password) sign-in ───
  const [portalOpen, setPortalOpen] = useState(false);
  const [portalMode, setPortalMode] = useState("login");
  const openPortal = (mode = "login") => { setPortalMode(mode); setPortalOpen(true); };

  // ─── Astrologer list ───
  const [astrologers, setAstrologers] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  useEffect(() => {
    api.get("/astrologers")
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        // Same ordering rule as AstrologersPage: live humans first, then AI.
        const liveHumans = list.filter((a) => !a.is_ai && (a.chat_enabled || a.call_enabled));
        const remainder  = list.filter((a) => !(!a.is_ai && (a.chat_enabled || a.call_enabled)));
        setAstrologers([...liveHumans, ...remainder]);
      })
      .catch(() => setAstrologers([]))
      .finally(() => setListLoading(false));
  }, []);

  const seoJsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQ_DATA.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  }), []);

  return (
    <PageShell>
      <SEO
        title="Free Astrology Chat AI — Sign in to AstroKuberChat"
        description="Free astrology chat AI with 20+ Vedic astrologers. Sign in to AstroKuberChat with Google, unlock your first 3 minutes free and chat instantly about marriage, career, love, money — powered by classical Parashari, KP, Nadi and Tajaka techniques."
        path="/login"
        keywords={[
          "free astrology chat ai", "astrology chat ai free", "ai astrology chat",
          "free ai astrologer chat", "chat with astrologer free ai",
          "astrokuberchat login", "vedic astrology ai chat free",
        ]}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(seoJsonLd) }} />

      {/* ============ HERO / LOGIN PROMPT ============ */}
      <section className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-10 pt-14 sm:pt-20 pb-10" data-testid="login-page">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 text-gold label-eyebrow">
            <Sparkle size={14} weight="fill" /> Free Astrology Chat AI · Sign in with Google
          </div>
          <h1
            className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 font-light tracking-tight leading-[1.1]"
            data-testid="login-heading"
          >
            Start your <span className="text-gold">free astrology chat AI</span> —
            <span className="block text-2xl sm:text-3xl lg:text-4xl mt-2 text-cream/90">
              first 3 minutes on every astrologer, free.
            </span>
          </h1>
          <p className="text-muted mt-5 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed font-light">
            AstroKuberChat is the world&apos;s first <strong className="text-cream">free astrology chat AI</strong> trained
            on 20+ classical Vedic techniques — Parashari, KP Sub-Lord, Nadi, Tajaka, D9 Navamsa,
            D10 Dasamsa and Kalachakra Dasha. Sign in with Google to unlock your
            free astrology chat AI session with any astrologer below.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={handleSignIn}
              className="btn-primary rounded-full px-6 sm:px-8 py-3 text-sm sm:text-base font-medium inline-flex items-center gap-2"
              data-testid="login-google-btn"
              aria-label="Login Via Google — free astrology chat AI"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#FFF" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#FFF" opacity=".85" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FFF" opacity=".7" d="M5.84 14.09A6.977 6.977 0 015.48 12c0-.73.13-1.44.36-2.09V7.07H2.18A11 11 0 001 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z"/>
                <path fill="#FFF" opacity=".55" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
              </svg>
              Login Via Google
            </button>
            <button
              onClick={() => openPortal("login")}
              className="rounded-full px-6 sm:px-8 py-3 text-sm sm:text-base font-medium inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 text-cream ring-1 ring-cream/25 backdrop-blur-sm transition-all"
              data-testid="login-portal-btn"
              aria-label="Login Via Portal — email and password"
            >
              <Envelope size={18} weight="fill" />
              Login Via Portal
            </button>
            <a
              href="#astrologer-grid"
              className="rounded-full px-6 py-3 text-sm font-medium text-muted hover:text-cream transition-colors underline decoration-dotted underline-offset-4"
              data-testid="login-jump-to-list"
            >
              Browse all astrologers ↓
            </a>
          </div>

          <p className="text-[11px] text-muted/70 mt-4 tracking-wide">
            By continuing you agree to our{" "}
            <a href="/terms" className="underline decoration-dotted">Terms</a>{" "}and{" "}
            <a href="/privacy" className="underline decoration-dotted">Privacy Policy</a>.
          </p>
        </div>

        {/* Trust strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-12 sm:mt-14">
          <TrustCard
            icon={<ChatCircleDots size={22} weight="duotone" className="text-gold" />}
            title="Free astrology chat AI"
            body="Every AI astrologer offers your first 3 minutes free — no credit card, no trial expiry. Chat instantly on marriage, career or love."
          />
          <TrustCard
            icon={<MoonStars size={22} weight="duotone" className="text-gold" />}
            title="Real Vedic techniques"
            body="Not a generic chatbot. Each free astrology chat AI is grounded in Parashari, KP, Nadi and D-charts."
          />
          <TrustCard
            icon={<ShieldCheck size={22} weight="duotone" className="text-gold" />}
            title="Secure & private"
            body="Google sign-in only. Your kundali and free astrology chat AI history stay yours."
          />
        </div>
      </section>

      {/* ============ ASTROLOGER GRID ============ */}
      <section id="astrologer-grid" className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 py-12 sm:py-16">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-gold label-eyebrow">
            <Star size={12} weight="fill" /> Pick your astrologer · free astrology chat AI
          </div>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl mt-3 font-light tracking-tight">
            20+ AI astrologers — <span className="text-gold">free chat</span> on your first 3 minutes
          </h2>
          <p className="text-muted mt-3 max-w-2xl mx-auto text-sm sm:text-base font-light leading-relaxed">
            Every card below opens a free astrology chat AI session. Choose by
            technique — marriage (D9 Navamsa), career (D10 Dasamsa), timing
            (Vimshottari Dasha), or annual (Tajaka Varshaphal).
          </p>
        </div>

        {listLoading ? (
          <div className="text-muted py-16 text-center">Loading astrologers…</div>
        ) : astrologers.length === 0 ? (
          <div className="text-muted py-16 text-center">
            Astrologers list is loading elsewhere.{" "}
            <Link to="/astrologers" className="text-gold underline decoration-dotted">
              Open the full directory →
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="login-astrologer-grid">
            {astrologers.map((a) => (
              <AstrologerMiniCard key={a.astrologer_id} a={a} firstChatFree={firstChatFree} />
            ))}
          </div>
        )}

        <div className="text-center mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={handleSignIn}
            className="btn-primary rounded-full px-6 sm:px-8 py-3 text-sm sm:text-base font-medium inline-flex items-center gap-2"
            data-testid="login-google-btn-secondary"
          >
            <Sparkle size={14} weight="fill" />
            Login Via Google
          </button>
          <button
            onClick={() => openPortal("login")}
            className="rounded-full px-6 sm:px-8 py-3 text-sm sm:text-base font-medium inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 text-cream ring-1 ring-cream/25 backdrop-blur-sm transition-all"
            data-testid="login-portal-btn-secondary"
          >
            <Envelope size={16} weight="fill" />
            Login Via Portal
          </button>
        </div>
      </section>

      {/* ============ WHY FREE ASTROLOGY CHAT AI STRIP ============ */}
      <section className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-10 py-10">
        <div className="card-surface rounded-2xl p-6 sm:p-10 border border-soft">
          <div className="grid sm:grid-cols-2 gap-6 sm:gap-10 items-center">
            <div>
              <div className="text-gold label-eyebrow flex items-center gap-2">
                <Lightning size={12} weight="fill" /> Why AstroKuberChat
              </div>
              <h3 className="font-serif-display text-2xl sm:text-3xl mt-3 font-light">
                The most trusted <span className="text-gold">free astrology chat AI</span> in India
              </h3>
              <p className="text-muted mt-4 text-sm leading-relaxed font-light">
                Traditional astrology consultations start at ₹1,500. Our free
                astrology chat AI gives you the same-quality reading — grounded in
                Parashari and KP — in your first 3 minutes, at no cost. If it
                feels valuable, extend at ₹15–55/min. If not, walk away.
              </p>
            </div>
            <ul className="space-y-3 text-sm">
              <BulletRow>3 minutes of <b>free astrology chat AI</b> on every astrologer.</BulletRow>
              <BulletRow>20+ AI astrologers, each specialised in one classical technique.</BulletRow>
              <BulletRow>Instant answers — no waiting, no scheduling.</BulletRow>
              <BulletRow>Your birth chart is saved for every future free astrology chat AI session.</BulletRow>
              <BulletRow>Google sign-in — no passwords, no OTPs.</BulletRow>
            </ul>
          </div>
        </div>
      </section>

      {/* ============ FAQ SECTION ============ */}
      <section className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-10 py-14 sm:py-20">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-gold label-eyebrow">
            <ChatCircleDots size={12} weight="fill" /> FAQ · Free astrology chat AI
          </div>
          <h2 className="font-serif-display text-3xl sm:text-4xl mt-3 font-light tracking-tight">
            Free astrology chat AI — everything you asked
          </h2>
        </div>

        <div className="space-y-3" data-testid="login-faq">
          {FAQ_DATA.map((f, i) => (
            <FaqItem key={i} q={f.q} a={f.a} />
          ))}
        </div>

        <div className="text-center mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={handleSignIn}
            className="btn-primary rounded-full px-6 sm:px-8 py-3 text-sm sm:text-base font-medium inline-flex items-center gap-2"
            data-testid="login-google-btn-faq"
          >
            <Sparkle size={14} weight="fill" />
            Login Via Google
          </button>
          <button
            onClick={() => openPortal("login")}
            className="rounded-full px-6 sm:px-8 py-3 text-sm sm:text-base font-medium inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 text-cream ring-1 ring-cream/25 backdrop-blur-sm transition-all"
            data-testid="login-portal-btn-faq"
          >
            <Envelope size={16} weight="fill" />
            Login Via Portal
          </button>
        </div>
      </section>

      {/* ============ FOOTER LINKS ============ */}
      <section className="text-center pb-16">
        <p className="text-xs text-muted">
          Are you an onboarded astrologer?{" "}
          <a href="/astrologer/login" className="text-gold underline decoration-dotted"
             data-testid="login-astrologer-portal-link">
            Sign in to the astrologer portal →
          </a>
        </p>
      </section>

      <PortalAuthModal
        open={portalOpen}
        onClose={() => setPortalOpen(false)}
        initialMode={portalMode}
        onSuccess={() => navigate("/astrologers", { replace: true })}
      />
    </PageShell>
  );
}

// ─── Sub-components ───
function TrustCard({ icon, title, body }) {
  return (
    <div className="card-surface rounded-md p-5 text-left">
      <div className="flex items-center gap-2">
        {icon}
        <div className="font-medium text-sm">{title}</div>
      </div>
      <p className="text-xs text-muted mt-2 font-light leading-relaxed">{body}</p>
    </div>
  );
}

function BulletRow({ children }) {
  return (
    <li className="flex items-start gap-3 text-cream/90 font-light">
      <span className="mt-1.5 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gold" />
      <span>{children}</span>
    </li>
  );
}

function AstrologerMiniCard({ a, firstChatFree }) {
  const liveHuman = !a.is_ai && (a.chat_enabled || a.call_enabled);
  return (
    <Link
      to={`/astrologers/${a.astrologer_id}`}
      className="group card-surface rounded-2xl overflow-hidden flex flex-col transition-transform hover:-translate-y-0.5 hover:shadow-xl"
      data-testid={`login-astrologer-card-${a.astrologer_id}`}
    >
      <div className="relative h-48">
        <img
          src={resolveAssetUrl(a.avatar)}
          alt={`${a.name} — free astrology chat AI, ${a.specialization || "Vedic"}`}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover object-top transition-transform group-hover:scale-[1.04]"
        />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-surface to-transparent" />
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
          {a.specialization && (
            <div className="px-2 py-1 text-[10px] label-eyebrow border border-soft bg-bg/85 rounded">
              {a.specialization}
            </div>
          )}
          {a.is_ai && (
            <div className="flex items-center gap-1.5 text-[10px] label-eyebrow text-gold border border-[#FFB800]/40 bg-bg/80 px-2 py-1 rounded">
              <Robot size={11} weight="fill" /> AI Expert
            </div>
          )}
        </div>
        {liveHuman && (
          <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 text-[10px] font-semibold px-2.5 py-1 rounded-full shadow-lg ring-1 ring-emerald-200/70 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            Live now
          </div>
        )}
        {a.is_ai && firstChatFree && (
          <div
            className="free-trial-badge absolute bottom-3 left-3 right-3 flex items-center justify-center gap-1.5 px-3 py-1.5 text-[11px] sm:text-[12px] font-extrabold tracking-wide rounded-full shadow-xl ring-2 ring-white/70 text-[#1A1410]"
            data-testid={`login-free-ribbon-${a.astrologer_id}`}
          >
            <Sparkle size={12} weight="fill" />
            <span>FREE CHAT · 3 MINUTES</span>
            <Sparkle size={12} weight="fill" />
          </div>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="font-serif-display text-xl leading-tight">{a.name}</div>
        {a.technique && (
          <div className="text-[11px] text-muted mt-1 line-clamp-1">{a.technique}</div>
        )}
        <div className="mt-auto pt-3 flex items-center justify-between">
          <div className="text-[11px] text-muted">
            {a.is_ai ? "AI · Vedic" : "Human · Vedic"}
          </div>
          <div className="text-[11px] font-semibold text-gold">
            {a.is_ai && firstChatFree ? "Free chat →" : "Open →"}
          </div>
        </div>
      </div>
    </Link>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`card-surface rounded-lg border border-soft transition-colors ${open ? "border-gold/40" : ""}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-5 py-4 flex items-start justify-between gap-4"
        data-testid={`login-faq-q-${slugify(q)}`}
        aria-expanded={open}
      >
        <span className="text-sm sm:text-base font-medium text-cream leading-snug">{q}</span>
        <CaretDown size={16} className={`text-gold flex-shrink-0 mt-1 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 -mt-1 text-sm text-muted font-light leading-relaxed"
             data-testid={`login-faq-a-${slugify(q)}`}>
          {a}
        </div>
      )}
    </div>
  );
}

function slugify(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 40);
}

// ─── FAQ data — keyword-dense on "free astrology chat AI" ───
const FAQ_DATA = [
  {
    q: "Is the free astrology chat AI on AstroKuberChat really free?",
    a: "Yes. Every AI astrologer on AstroKuberChat offers a free astrology chat AI session — the first 3 minutes are completely free, on the house, no card required. After 3 minutes, if you want to continue the free astrology chat AI beyond the trial, the astrologer's per-minute rate (₹15–55/min) kicks in and you can top up whenever you like.",
  },
  {
    q: "How is your free astrology chat AI different from generic AI chatbots?",
    a: "A generic chatbot guesses. Our free astrology chat AI is grounded in classical Vedic techniques — Parashari, KP Sub-Lord, Nadi, Tajaka, D9 Navamsa, D10 Dasamsa and Kalachakra Dasha — computed live from your actual birth chart using Swiss Ephemeris. Every answer in the free astrology chat AI cites the technique it used, so nothing is made up.",
  },
  {
    q: "Which questions can I ask in the free astrology chat AI?",
    a: "Anything you'd ask a real Vedic astrologer — marriage timing, career direction, D9 Navamsa marriage compatibility, D10 Dasamsa career check, financial dashas, love and relationships, health windows, Rahu-Ketu placement, and remedies. Different astrologers specialise in different areas, so pick the free astrology chat AI whose technique matches your question.",
  },
  {
    q: "Do I need my birth time to use the free astrology chat AI?",
    a: "Ideally yes — an accurate birth time makes the free astrology chat AI dramatically more precise (it enables the Ascendant, D-charts and Dasha timing). If you don't know your exact time, you can still start the free astrology chat AI with a rough window; the astrologer will do a light rectification and flag any low-confidence sections.",
  },
  {
    q: "Do I need to pay or enter card details to start the free astrology chat AI?",
    a: "No. Sign in with Google, pick any AI astrologer and the free astrology chat AI begins immediately. Payment is only asked for if you choose to continue the conversation past the 3-minute free window.",
  },
  {
    q: "Can I chat with human astrologers, or is it only free astrology chat AI?",
    a: "Both. AstroKuberChat blends free astrology chat AI (instant, 24×7, first 3 min free) with a small panel of live human astrologers you can call or chat with when you want a human touch. Live humans appear at the top of the list with a green 'Live now' pill.",
  },
  {
    q: "Is my data safe when I use the free astrology chat AI?",
    a: "Yes. We use Google sign-in only — we never store passwords. Your birth details and free astrology chat AI history are visible only to you, encrypted at rest, and never sold or shared with third parties.",
  },
  {
    q: "Can I use the free astrology chat AI on mobile?",
    a: "Yes. AstroKuberChat is mobile-first — the free astrology chat AI works fully in any mobile browser (no app install needed) and remembers your birth chart across devices once you sign in with Google.",
  },
];
